/**
 * useWizardAutoSave Hook
 * 
 * Provides automatic draft saving functionality for multi-step wizards.
 * Follows the pattern established in RequestTypeFormBuilder.
 * 
 * Usage:
 * ```tsx
 * const { saveDraft, isSaving } = useWizardAutoSave({
 *   draftKey: 'wizard_space_discovery',
 *   entityId: 'company_123',
 *   accessToken,
 *   getData: () => ({ step: currentStep, answers: wizardData }),
 * });
 * 
 * // Save when moving to next step
 * await saveDraft();
 * ```
 */

import { useState } from 'react';
import { toast } from 'sonner';
import { projectId } from '../utils/supabase/info';

interface UseWizardAutoSaveOptions {
  /** Unique key for this wizard type (e.g., 'wizard_space_discovery', 'wizard_export') */
  draftKey: string;
  
  /** Company or entity ID to associate the draft with */
  entityId: string;
  
  /** Access token for authentication */
  accessToken: string;
  
  /** Function that returns current wizard data to save */
  getData: () => any;
  
  /** Optional callback when draft is successfully saved */
  onDraftSaved?: (draftId: string) => void;
  
  /** Custom save endpoint (defaults to /wizard-draft) */
  endpoint?: string;
}

export function useWizardAutoSave({
  draftKey,
  entityId,
  accessToken,
  getData,
  onDraftSaved,
  endpoint = '/make-server-e90c59cd/wizard-draft'
}: UseWizardAutoSaveOptions) {
  const [isSaving, setIsSaving] = useState(false);
  const [draftId, setDraftId] = useState<string | null>(null);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);

  /**
   * Save current wizard state as draft
   */
  const saveDraft = async (showToast: boolean = true): Promise<boolean> => {
    const data = getData();
    
    // Don't save if no data
    if (!data || Object.keys(data).length === 0) {
      return false;
    }

    setIsSaving(true);

    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1${endpoint}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            draftKey,
            entityId,
            data,
            draftId: draftId || undefined,
          }),
        }
      );

      if (response.ok) {
        const result = await response.json();
        const savedId = result.draftId || result.id;
        
        if (!draftId) {
          setDraftId(savedId);
        }
        
        setLastSaved(new Date());
        
        if (onDraftSaved) {
          onDraftSaved(savedId);
        }
        
        if (showToast) {
          toast.success('Progress auto-saved');
        }
        
        return true;
      } else {
        console.error('Failed to save draft:', await response.text());
        if (showToast) {
          toast.error('Failed to auto-save progress');
        }
        return false;
      }
    } catch (error) {
      console.error('Error saving draft:', error);
      if (showToast) {
        toast.error('Failed to auto-save progress');
      }
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Load existing draft
   */
  const loadDraft = async (): Promise<any | null> => {
    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1${endpoint}/${draftKey}/${entityId}`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        const result = await response.json();
        if (result.draft) {
          setDraftId(result.draft.id);
          setLastSaved(new Date(result.draft.updatedAt));
          return result.draft.data;
        }
      }
      
      return null;
    } catch (error) {
      console.error('Error loading draft:', error);
      return null;
    }
  };

  /**
   * Delete current draft
   */
  const deleteDraft = async (): Promise<boolean> => {
    if (!draftId) return false;

    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1${endpoint}/${draftId}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        setDraftId(null);
        setLastSaved(null);
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Error deleting draft:', error);
      return false;
    }
  };

  return {
    saveDraft,
    loadDraft,
    deleteDraft,
    isSaving,
    draftId,
    lastSaved,
  };
}

/**
 * Debounced auto-save hook for continuous saving
 * 
 * Usage:
 * ```tsx
 * useWizardAutoSaveDebounced({
 *   ...autoSaveOptions,
 *   dependencies: [formData, currentStep],
 *   debounceMs: 5000,
 * });
 * ```
 */
export function useWizardAutoSaveDebounced(
  options: UseWizardAutoSaveOptions & {
    dependencies: any[];
    debounceMs?: number;
    enabled?: boolean;
  }
) {
  const { dependencies, debounceMs = 5000, enabled = true, ...autoSaveOptions } = options;
  const { saveDraft, ...rest } = useWizardAutoSave(autoSaveOptions);

  // Auto-save on dependency changes (debounced)
  React.useEffect(() => {
    if (!enabled) return;

    const timer = setTimeout(() => {
      saveDraft(false); // Don't show toast for automatic saves
    }, debounceMs);

    return () => clearTimeout(timer);
  }, dependencies);

  return { saveDraft, ...rest };
}

// Re-export React for the debounced hook
import * as React from 'react';
